import { observer } from "mobx-react";
import React, { useCallback, useMemo, useState } from "react";
import { useLink } from "Shared/Components/Link/useLink";

import { Button, Menu, MenuPopover, MenuTrigger } from "@fluentui/react-components";

import { AppLeftBarSubMenu } from "../AppLeftBarSubMenu/AppLeftBarSubMenu";
import { useAppLeftBarItemStyles } from "./AppLeftBarItem.styles";
import { AppLeftBarItemProps } from "./AppLeftBarItem.types";

const AppLeftBarItemInRouteBase = (props: AppLeftBarItemProps) => {
    const { item, matchedItem, onDismiss, active } = props;
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [isMenuOver, setIsMenuOver] = useState(false);
    const [isHideMenu, setIsHideMenu] = useState(true);

    const selected = useMemo(() => matchedItem.itemKey === item.displayName || !!active, [item.displayName, matchedItem.itemKey, active]);

    const classNames = useAppLeftBarItemStyles({ selected: selected, isMouseOver: isMouseOver });

    const onMouseEnter = useCallback(() => {
        setIsMouseOver(true);
        setIsHideMenu(false);
    }, []);

    const onMouseLeave = useCallback(() => {
        setIsMouseOver(false);
    }, []);

    const onMenuEnter = useCallback(() => {
        setIsMouseOver(true);
        setIsMenuOver(true);
        setIsHideMenu(false);
    }, []);

    const onMenuLeave = useCallback(() => {
        setIsMouseOver(false);
        setIsMenuOver(false);
    }, []);

    const dismissMenu = useCallback(() => {
        setIsHideMenu(true);
        setIsMenuOver(false);
        onDismiss?.();
    }, [onDismiss]);

    const isOpen = useMemo(() => !isHideMenu && (isMouseOver || isMenuOver), [isHideMenu, isMenuOver, isMouseOver]);

    const renderMenuIcon = useMemo(
        () => (
            <>
                {selected && !active && <div className={classNames.selectedBar} role="none" />}
                {item.regularIcon &&
                    item.filledIcon &&
                    (selected || isMouseOver ? (
                        <item.filledIcon className={classNames.leftBarIcon} />
                    ) : (
                        <item.regularIcon className={classNames.leftBarIcon} />
                    ))}
                <div className={classNames.leftBarTitle} role="none">
                    {item.displayName}
                </div>
            </>
        ),
        [classNames.selectedBar, classNames.leftBarIcon, classNames.leftBarTitle, selected, item, isMouseOver, active]
    );

    const { onClick, onKeyDown, href } = useLink(item.routeName ?? "", item.isV1, false);

    if (item.subItems) {
        return (
            <Menu positioning="after-top" openOnHover open={isOpen}>
                <MenuTrigger>{renderMenuIcon}</MenuTrigger>
                <MenuPopover className={classNames.menuPopUp} onMouseEnter={onMenuEnter} onMouseLeave={onMenuLeave}>
                    <AppLeftBarSubMenu subItems={item.subItems} matchedItem={matchedItem} onDismiss={dismissMenu} />
                </MenuPopover>
            </Menu>
        );
    }

    return item.isNotLink ? (
        <Button
            className={classNames.root}
            onKeyDown={onKeyDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onMenuEnter}
            onBlur={onMenuLeave}
            appearance="transparent"
            aria-label={item.description ? `${item.displayName}, ${item.description}` : item.displayName}
        >
            {renderMenuIcon}
        </Button>
    ) : (
        <a
            className={classNames.root}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={item.description ? `${item.displayName}, ${item.description}` : item.displayName}
            href={href}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onMenuEnter}
            onBlur={onMenuLeave}
        >
            {renderMenuIcon}
        </a>
    );
};

export const AppLeftBarItemInRoute = observer(AppLeftBarItemInRouteBase);
